
import React, { useState, useRef } from 'react';
import { Website, Lead, Page, Section } from '../types';
import { VisualEditor } from '../components/VisualEditor';
import { Icons } from '../constants';

interface EditorPageProps {
  website: Website;
  onUpdate: (updated: Website) => void;
  onBack: () => void;
  onCaptureLead: (lead: Lead) => void;
}

export const Editor: React.FC<EditorPageProps> = ({ website, onUpdate, onBack, onCaptureLead }) => {
  const [activePageIndex, setActivePageIndex] = useState(0);
  const [showPageManager, setShowPageManager] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleUpdatePage = (updatedPage: Page) => {
    const newPages = [...website.pages];
    newPages[activePageIndex] = updatedPage;
    onUpdate({ ...website, pages: newPages });
  };

  const handleAddHtmlPages = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    
    const newPages: Page[] = [];
    for (let i = 0; i < files.length; i++) {
      const text = await files[i].text();
      const fileName = files[i].name;
      
      const parser = new DOMParser();
      const doc = parser.parseFromString(text, 'text/html');
      const bodyContent = doc.body.innerHTML;
      
      newPages.push({
        name: fileName.replace('.html', '').charAt(0).toUpperCase() + fileName.replace('.html', '').slice(1),
        path: fileName === 'index.html' ? `/new-${Date.now()}` : `/${fileName}`,
        sections: [{
          id: `sec-${Math.random().toString(36).substr(2, 5)}`,
          type: 'raw',
          content: { html: bodyContent, title: 'Imported Content' }
        }]
      });
    }

    onUpdate({
      ...website,
      pages: [...website.pages, ...newPages]
    });
    
    // Switch to the first newly added page
    setActivePageIndex(website.pages.length);
    setShowPageManager(false);
  };

  return (
    <div className="flex flex-col h-full overflow-hidden bg-slate-50">
      <div className="bg-white border-b border-slate-200 px-8 py-4 flex items-center justify-between sticky top-0 z-[60] shadow-sm">
        <div className="flex items-center gap-8">
          <button onClick={onBack} className="flex items-center gap-2 group">
             <div className="w-10 h-10 rounded-2xl bg-slate-50 flex items-center justify-center text-slate-400 group-hover:bg-indigo-50 group-hover:text-indigo-600 transition-all">
               <Icons.Undo className="w-5 h-5 rotate-180" />
             </div>
             <div>
               <p className="text-[10px] font-black uppercase text-slate-400 tracking-tighter leading-none">Back to</p>
               <p className="text-sm font-bold text-slate-900 leading-none mt-1">Dashboard</p>
             </div>
          </button>

          <div className="h-10 w-px bg-slate-100 mx-2"></div>

          <div className="flex flex-col">
            <h1 className="font-black text-xl text-slate-900 tracking-tight leading-none">{website.name}</h1>
            <div className="flex items-center gap-2 mt-1.5">
              <div className="w-1.5 h-1.5 rounded-full bg-emerald-500"></div>
              <p className="text-[10px] text-slate-400 uppercase font-bold tracking-widest">Editing {website.pages[activePageIndex]?.name || 'Page'}</p>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-4">
          <div className="bg-slate-50 p-1.5 rounded-2xl flex items-center gap-1 border border-slate-100 max-w-[400px] overflow-x-auto no-scrollbar">
             {website.pages.map((page, idx) => (
               <button 
                key={`${page.name}-${idx}`}
                onClick={() => setActivePageIndex(idx)}
                className={`px-5 py-2.5 rounded-xl text-xs font-black transition-all whitespace-nowrap ${
                  activePageIndex === idx ? 'bg-white shadow-xl shadow-indigo-100/50 text-indigo-600' : 'text-slate-400 hover:text-slate-600 hover:bg-white/50'
                }`}
               >
                 {page.name}
               </button>
             ))}
             <button 
               onClick={() => setShowPageManager(!showPageManager)}
               className="p-2.5 text-slate-300 hover:text-indigo-600 transition-colors shrink-0"
               title="Page Manager"
             >
               <Icons.Settings className="w-4 h-4" />
             </button>
          </div>

          <div className="h-8 w-px bg-slate-100 mx-2"></div>

          <button className="bg-slate-900 text-white px-8 py-3.5 rounded-2xl font-black text-sm hover:bg-indigo-600 hover:-translate-y-0.5 active:translate-y-0 shadow-2xl shadow-indigo-100/20 transition-all flex items-center gap-3">
            Publish Site
          </button>
        </div>
      </div>
      
      <div className="flex-1 flex overflow-hidden relative">
        <div className="flex-1 overflow-y-auto p-12 custom-scrollbar relative bg-slate-50/50">
          <div className="max-w-7xl mx-auto pb-32">
            {website.pages[activePageIndex] ? (
              <VisualEditor 
                website={website} 
                activePage={website.pages[activePageIndex]}
                onUpdatePage={handleUpdatePage} 
              />
            ) : (
              <div className="flex flex-col items-center justify-center h-[600px] text-slate-400">
                <Icons.Globe className="w-12 h-12 mb-4 opacity-20" />
                <p className="font-bold">No page selected</p>
              </div>
            )}
          </div>
        </div>

        {showPageManager && (
          <div className="absolute top-4 right-8 w-80 bg-white rounded-[2.5rem] border border-slate-200 shadow-2xl p-8 z-[70] animate-in slide-in-from-top-4">
             <div className="flex justify-between items-center mb-6">
                <h3 className="text-sm font-black uppercase text-slate-900 tracking-widest">Page Explorer</h3>
                <button onClick={() => setShowPageManager(false)} className="text-slate-300 hover:text-rose-500">
                   <Icons.Trash className="w-4 h-4" />
                </button>
             </div>
             
             <div className="space-y-3 max-h-[400px] overflow-y-auto custom-scrollbar pr-2">
                {website.pages.map((page, idx) => (
                  <div 
                    key={`${page.path}-${idx}`} 
                    className={`p-4 rounded-2xl border transition-all cursor-pointer ${activePageIndex === idx ? 'bg-indigo-50 border-indigo-200' : 'bg-slate-50 border-slate-100 hover:border-indigo-100'}`}
                    onClick={() => { setActivePageIndex(idx); setShowPageManager(false); }}
                  >
                    <div className="flex items-center justify-between">
                      <span className={`text-xs font-black ${activePageIndex === idx ? 'text-indigo-600' : 'text-slate-700'}`}>{page.name}</span>
                      <span className="text-[10px] font-mono text-slate-400">{page.path}</span>
                    </div>
                  </div>
                ))}
             </div>

             <div className="mt-8 pt-6 border-t border-slate-100 space-y-3">
                <input 
                  type="file" 
                  ref={fileInputRef} 
                  multiple 
                  accept=".html" 
                  className="hidden" 
                  onChange={handleAddHtmlPages} 
                />
                <button 
                  onClick={() => fileInputRef.current?.click()}
                  className="w-full py-4 bg-indigo-600 text-white rounded-2xl text-[10px] font-black uppercase hover:bg-indigo-700 transition-all flex items-center justify-center gap-2 shadow-lg shadow-indigo-100"
                >
                   <Icons.Plus className="w-3 h-3" /> Upload More HTML Files
                </button>
             </div>
          </div>
        )}
      </div>
    </div>
  );
};
